/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import TextDiv from '@/components/text-div';
import PermissionGate from '@/components/permission-gate';
import { ThemeContext } from '@/contexts/theme-context';
import { TelemetryContext } from '@/contexts/telemetry-context';

export default function ToggleSwitch({
  inputSelectorId,
  onChange,
  label,
  defaultValue,
  loading,
  controlled,
  marginBottom,
  subControls,
  generateControl,
  mainControlHidden,
  telemetryToggleOnEvent,
  telemetryOnChangeEvent,
  telemetryValue,
  telemetryCustomProperty,
  addChangeOnRender,
  ariaLabel,
  permissionId,
}) {
  const { primaryColor } = useContext(ThemeContext);
  const telemetryContext = useContext(TelemetryContext);

  const [toggledOn, setToggledOn] = useState(defaultValue);

  const onSwitchClicked = (event) => {
    setToggledOn(event.target.checked);
    if (onChange) {
      onChange(event.target.checked);
    }
    if (telemetryOnChangeEvent) {
      telemetryContext?.sendEvent(telemetryOnChangeEvent, {
        ...telemetryValue && { value_changed: telemetryValue },
        ...telemetryCustomProperty && { [telemetryCustomProperty]: event.target.checked },
      });
    }
    if (telemetryToggleOnEvent && event.target.checked) {
      telemetryContext?.sendEvent(telemetryToggleOnEvent);
    }
  };

  let toggleValue = toggledOn;
  if (controlled) {
    toggleValue = defaultValue;
  }

  useEffect(() => {
    if (addChangeOnRender) {
      onChange(toggledOn ?? false);
    }
  }, [addChangeOnRender]);

  return (
    <>
      <div className="wrapper">
        {!mainControlHidden && (
          <div className="toggle-wrapper">
            {label && (
              <div className="switch-label"><TextDiv>{label}</TextDiv></div>
            )}
            <label className="switch">
              <PermissionGate permission={permissionId}>
                <input aria-label={ariaLabel} id={inputSelectorId} type="checkbox" checked={toggleValue} onChange={onSwitchClicked} disabled={loading} />
                <span className={`slider round ${loading ? 'loading' : ''}`} />
              </PermissionGate>
            </label>
          </div>
        )}
        {(toggleValue || mainControlHidden) && generateControl && subControls.map((control) => (
          <div id="sub-control" key={control.id}>
            {generateControl(control, generateControl)}
          </div>
        ))}
      </div>
      <style jsx>
        {`

          .wrapper {
            display: flex;
            flex-direction: column;
            width: 100%;
          }
          .toggle-wrapper {
            display: flex;
            flex-direction: row;
            width: 100%;
            margin-bottom: ${marginBottom || (subControls?.length ? 24 : 0)}px;
          }

          .switch-label {
            display: flex;
            align-items: center;
            font-size: var(--font-size-tooltip);
            font-weight: var(--font-weight-H1);
            flex: 1;
          }

          .switch {
            position: relative;
            display: inline-block;
            width: 56px;
            height: 26px;
            margin-left: 14px;
          }
         
          .toggle-wrapper:has(.slider) .switch-label {
            border-right: 1px solid var(--color-line-grey);
          }

          .switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }

          .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: var(--color-mid-grey);
            transition: .4s;
          }

          .slider:before {
            position: absolute;
            content: "";
            height: 28px;
            width: 28px;
            left: -1px;
            bottom: -1px;
            background-color: var(--color-white);
            transition: .2s;
          }

          input:checked + .slider {
            background-color: var(--color-green);
          }

          input:focus + .slider {
            box-shadow: 0 0 1px var(--color-green);
          }

          input:checked + .slider:before {
            transform: translateX(30px);
          }

          input:disabled + .slider {
            opacity: 0.7;
            cursor: not-allowed;
          }

          .slider.round {
            border-radius: 34px;
          }

          .slider.round:before {
            border-radius: 50%;
            box-shadow: 0 2px 4px 0 var(--color-card-shadow);
          }

          .slider.loading:after {
            content: "";
            display: block;
            width: 16px;
            height: 16px;
            margin: 4px;
            border-radius: 50%;
            border: 1px solid;
            border-color: ${primaryColor} transparent ${primaryColor} transparent;
            animation: rotate 1.2s linear infinite;
            transition: .2s;
          }

          input:checked + .slider.loading:after {
            margin-left: 34px;
          }

          #sub-control {
            margin-bottom: 25px;
          }

          @keyframes rotate {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </>
  );
}

ToggleSwitch.defaultProps = {
  inputSelectorId: null,
  onChange: null,
  label: null,
  subControls: [],
  generateControl: null,
  defaultValue: false,
  loading: false,
  controlled: false,
  mainControlHidden: false,
  telemetryToggleOnEvent: null,
  telemetryOnChangeEvent: null,
  telemetryValue: null,
  telemetryCustomProperty: null,
  marginBottom: null,
  addChangeOnRender: false,
  ariaLabel: null,
};

ToggleSwitch.propTypes = {
  addChangeOnRender: PropTypes.bool,
  inputSelectorId: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  loading: PropTypes.bool,
  controlled: PropTypes.bool,
  subControls: PropTypes.arrayOf(PropTypes.shape()),
  generateControl: PropTypes.func,
  defaultValue: PropTypes.bool,
  mainControlHidden: PropTypes.bool,
  telemetryToggleOnEvent: PropTypes.string,
  telemetryOnChangeEvent: PropTypes.string,
  telemetryValue: PropTypes.string,
  telemetryCustomProperty: PropTypes.string,
  marginBottom: PropTypes.number,
  ariaLabel: PropTypes.string,
};
